/**
 * SideMenu Component
 *
 * A reusable sidebar navigation component that matches the Ideoz design system.
 * Supports collapsible/expandable states, responsive design, accessibility features,
 * and dynamic navigation items.
 *
 * @module @ideoz/ui/components/molecules/SideMenu
 */

import React, { useState, useCallback, KeyboardEvent, useRef, useEffect } from 'react';
import { cn } from '../atoms/utils';
import { Badge } from '../atoms/badge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../atoms/tooltip';
import { UserMenuTemplate } from '../../components/templates/UserMenuTemplate';
import Icon from './Icon';
import type {
  SideMenuProps,
  SideMenuItemProps,
  SideMenuSectionProps,
  NavigationItem,
  UserMenuItem,
  User,
} from '../../types/SideMenu.types';
import MiniLogo from '../atoms/MiniLogo';
import Logo from '../atoms/Logo';
import { PanelLeft, Info } from 'lucide-react';

import { useAppSelector } from '@ideoz/core';
/**
 * Reusable base styles for interactive menu items
 * Used across navigation items, social links, and other interactive menu elements
 */
const MENU_ITEM_BASE_CLASSES =
  'min-w-10 min-h-10 px-2 group relative flex items-center gap-2 py-2 rounded-lg cursor-pointer transition-all duration-150 ease-out hover:bg-blue-light-100/60 hover:text-blue-light-600';

/**
 * Individual navigation item component
 */
const SideMenuItem: React.FC<SideMenuItemProps> = ({
  item,
  isCollapsed,
  isActive,
  onClick,
  testId,
}) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  const itemContent = (
    <button
      role="button"
      tabIndex={0}
      className={cn(
        MENU_ITEM_BASE_CLASSES,
        isActive ? 'bg-blue-light-100/80 text-blue-light-600' : '',
        // Disabled styles
        item.disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
        // Dynamic padding based on state
        isCollapsed ? 'justify-center items-center w-10 rounded-3xl before:content-[""]' : ''
      )}
      data-active={isActive}
      onClick={item.disabled ? undefined : onClick}
      onKeyDown={item.disabled ? undefined : handleKeyDown}
      aria-label={item.label}
      aria-current={isActive ? 'page' : undefined}
      aria-disabled={item.disabled}
      data-testid={testId}
    >
      {/* Icon - always visible with smooth transition */}
      {item.icon && (
        <Icon
          icon={item.icon as any}
          size={20}
          className={cn('transition-colors duration-150 flex-shrink-0 stroke-[currentColor]')}
        />
      )}
      {/* Label with smooth fade + width animation */}
      <div
        className={cn(
          'flex items-center justify-between flex-1 whitespace-nowrap',
          isCollapsed ? 'w-0 opacity-0 flex-none' : 'w-auto opacity-100'
        )}
      >
        <span className={cn('truncate text-sm')}>{item.label}</span>
        {item.badge && <Badge variant="secondary">{item.badge}</Badge>}
      </div>
    </button>
  );

  // Wrap with tooltip when collapsed
  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{itemContent}</TooltipTrigger>
        <TooltipContent className="px-2 py-1 text-xs" side="right">
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return itemContent;
};

/**
 * Section wrapper component for grouping navigation items
 */
const SideMenuSection: React.FC<SideMenuSectionProps> = ({
  title,
  children,
  isCollapsed,
  className,
}) => {
  return (
    <div className={cn('space-y-1', className)}>
      {title && !isCollapsed && (
        <div className="px-3 py-2">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</h3>
        </div>
      )}
      {children}
    </div>
  );
};

/**
 * User avatar component
 * Renders user avatar with fallback to initials
 */
interface UserAvatarProps {
  user: User;
  isCollapsed?: boolean;
  className?: string;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ user, isCollapsed, className }) => {
  return (
    <div
      className={cn(
        'w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden bg-primary-900',
        className
      )}
    >
      {user.avatar ? (
        <img
          src={user.avatar}
          alt={user.name || 'User'}
          className="w-7 h-7 rounded-full object-cover"
        />
      ) : (
        <span className="text-white text-xs font-medium">
          {user.name ? user.name.charAt(0).toUpperCase() : 'ID'}
        </span>
      )}
    </div>
  );
};

/**
 * User info component
 * Renders user name and plan with fade animation
 */
interface UserInfoProps {
  user: User;
  isCollapsed?: boolean;
  showPlan?: boolean;
  className?: string;
}
const UserInfo: React.FC<UserInfoProps> = ({ user, isCollapsed, showPlan = true, className }) => {
  const planDetails = useAppSelector((state: any) => state.app.planDetails);
  const currentPlanName = planDetails?.name || 'Free';

  return (
    <div
      className={cn(
        'flex flex-col items-start min-w-0 overflow-hidden',
        'transition-all duration-150 ease-out',
        isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100',
        className
      )}
    >
      <span className="text-gray-700 truncate text-sm whitespace-nowrap">{user.name}</span>
      {showPlan && (
        <span className="text-xs font-normal text-gray-400 truncate whitespace-nowrap">
          {currentPlanName} plan
        </span>
      )}
    </div>
  );
};

/**
 * Main SideMenu component
 */
export const SideMenu: React.FC<SideMenuProps> = ({
  items,
  isCollapsed = false,
  onToggleCollapse,
  currentPath,
  socialLinks = [],
  user,
  onLogoClick,
  userMenuProps,
  className,
  testId = 'side-menu',
}) => {
  // State for user menu
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  // Ref to the user menu panel for focus management
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  // Group navigation items by type
  const navigationItems = items.filter((item) => !item.type || item.type === 'navigation');
  const modeItems = items.filter((item) => item.type === 'mode');
  const actionItems = items.filter((item) => item.type === 'action');

  // Handle user avatar click
  const handleUserAvatarClick = useCallback(() => {
    if (userMenuProps && user?.isLoggedin) {
      setIsUserMenuOpen(true);
    }
  }, [userMenuProps, user?.isLoggedin]);

  // Handle user menu close
  const handleUserMenuClose = useCallback(() => {
    setIsUserMenuOpen(false);
  }, []);

  // Close on Escape and manage focus when menu opens
  useEffect(() => {
    if (!isUserMenuOpen) return;

    const onKeyDown = (e: KeyboardEvent | globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        handleUserMenuClose();
      }
    };

    // Move focus to the menu container for accessibility
    setTimeout(() => {
      try {
        userMenuRef.current?.focus();
      } catch (err) {
        // ignore
      }
    }, 0);

    document.addEventListener('keydown', onKeyDown as any);
    return () => {
      document.removeEventListener('keydown', onKeyDown as any);
    };
  }, [isUserMenuOpen, handleUserMenuClose]);

  const handleItemClick = useCallback((item: NavigationItem) => {
    if (item.disabled) return;

    if (item.onClick) {
      item.onClick();
    }

    // Handle navigation if path is provided
    // Only use pushState in web context (not in plugin where origin is null/data:)
    if (item.path && typeof window !== 'undefined') {
      try {
        // Check if we're in a plugin context (data: URL or null origin)
        const isPluginContext =
          window.location.origin === 'null' ||
          window.location.protocol === 'data:' ||
          window.location.href.startsWith('data:');

        // Only use pushState if we're not in a plugin context and have a valid path
        if (!isPluginContext && item.path) {
          window.history.pushState({}, '', item.path);
        }
      } catch (error) {
        // Silently fail in plugin context where pushState is not supported
        console.debug('pushState not available in plugin context:', error);
      }
    }
  }, []);

  const isItemActive = useCallback(
    (item: NavigationItem) => {
      // If isActive is explicitly defined (true or false), use that value directly
      if (item.isActive !== undefined) {
        return item.isActive;
      }
      // If isActive is undefined, check path matching as fallback
      return currentPath ? item.path === currentPath : false;
    },
    [currentPath]
  );

  return (
    <TooltipProvider>
      <aside
        className={cn(
          // Base styles
          'flex flex-col glass glass-glow py-4 fixed left-2 top-2 bottom-2 z-40 transition-[width] duration-200 ease-out',
          isCollapsed ? 'w-16 rounded-2xl' : 'w-[260px] rounded-2xl',
          className
        )}
        aria-label="Main navigation"
        data-testid={testId}
      >
        {/* Header with cross-fade logo animation */}
        <header className="h-[52px] relative flex-shrink-0">
          {/* Collapsed state - Mini logo button that expands sidebar */}
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-center',
              'transition-opacity duration-150 ease-out',
              isCollapsed ? 'opacity-100' : 'opacity-0 pointer-events-none'
            )}
          >
            <button
              onClick={onToggleCollapse}
              className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 transition-colors duration-150 group cursor-e-resize"
              aria-label="Expand sidebar"
              data-testid="sidebar-toggle-expand"
              data-track="sidebar_toggle"
            >
              <MiniLogo
                width={28}
                height={28}
                className="w-5 h-5 text-gray-500 group-hover:hidden"
              />
              <PanelLeft className="w-5 h-5 text-gray-500 hidden group-hover:block" />
            </button>
          </div>

          {/* Expanded state - Full logo + collapse button */}
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-between px-5',
              'transition-opacity duration-150 ease-out',
              isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'
            )}
          >
            <button onClick={onLogoClick ? () => onLogoClick() : undefined} data-track="sidebar_logo">
              <Logo width={80} height={20} />
            </button>
            <button
              onClick={onToggleCollapse}
              className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 transition-colors duration-150 cursor-w-resize"
              aria-label="Collapse sidebar"
              data-testid="sidebar-toggle-collapse"
              data-track="sidebar_toggle"
            >
              <PanelLeft className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </header>

        {/* Navigation Content */}
        <nav className={cn('flex-1 overflow-y-auto my-8 flex gap-1 flex-col px-3')}>
          {/* All Navigation Items (simplified layout) */}
          {[...navigationItems, ...modeItems, ...actionItems].map((item) => {
            const itemType = item.type || 'navigation';
            const testIdPrefix =
              itemType === 'navigation' ? 'nav' : itemType === 'mode' ? 'mode' : 'action';
            return (
              <SideMenuItem
                key={item.id}
                item={item}
                isCollapsed={isCollapsed}
                isActive={isItemActive(item)}
                onClick={() => handleItemClick(item)}
                testId={`${testIdPrefix}-item-${item.id}`}
              />
            );
          })}
        </nav>

        {/* Footer with Help Icon and User Profile */}
        {user && user.isLoggedin && (
          <footer className="flex gap-1 flex-col px-3">
            {/* Help & Documentation Icon */}
            <a
              href="https://ideoz.ai/documentation/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Help & Documentation"
              data-testid="sidebar-help-docs"
              data-track="help_docs"
              className={cn(
                MENU_ITEM_BASE_CLASSES,
                isCollapsed ? 'justify-center items-center w-10 rounded-3xl before:content-[""]' : ''
              )}
            >
              <Icon
                icon={Info as any}
                size={20}
                className={cn('transition-colors duration-150 flex-shrink-0 stroke-[currentColor]')}
              />
              <div
                className={cn(
                  'flex items-center justify-between flex-1 whitespace-nowrap',
                  isCollapsed ? 'w-0 opacity-0 flex-none' : 'w-auto opacity-100'
                )}
              >
                <span className={cn('truncate text-sm')}>Help & Docs</span>
              </div>
            </a>

            {/* User Profile - only show if logged in. Clicking opens the user menu */}
            <button
              type="button"
              onClick={handleUserAvatarClick}
              aria-label={user.name ? `${user.name}'s menu` : 'User menu'}
              aria-expanded={isUserMenuOpen}
              aria-controls="sidebar-user-menu"
              data-testid="sidebar-user-avatar"
              className={cn(
                MENU_ITEM_BASE_CLASSES,
                isCollapsed ? 'justify-center items-center w-10 before:content-[\"\"]' : ''
              )}
            >
              <UserAvatar user={user} isCollapsed={isCollapsed} />
              <UserInfo user={user} isCollapsed={isCollapsed} />
            </button>
          </footer>
        )}
      </aside>

      {/* Render the user menu as a fixed panel to the right of the sidebar */}
      {isUserMenuOpen && user && user.isLoggedin && userMenuProps && (
        <>
          {/* Backdrop: catches outside clicks and provides a target to close the menu */}
          <div
            onClick={handleUserMenuClose}
            aria-hidden="true"
            className="fixed inset-0 z-10 bg-transparent"
          />

          <div
            id="sidebar-user-menu"
            role="dialog"
            aria-modal="true"
            aria-label="User menu"
            ref={userMenuRef}
            tabIndex={-1}
            className={cn(`fixed top-0 h-full z-40 transition-[left] duration-200 ease-out`)}
            style={{
              left: isCollapsed ? '64px' : '260px', // Sidebar width (w-16 = 64px collapsed, w-[260px] = 260px expanded)
            }}
          >
            <UserMenuTemplate
              onClose={handleUserMenuClose}
              points={userMenuProps.points}
              pointsLabel={userMenuProps.pointsLabel}
              menuItems={userMenuProps.menuItems}
              socialLinks={[]}
              onRequestUpgrade={userMenuProps.onRequestUpgrade}
              isRequestingUpgrade={userMenuProps.isRequestingUpgrade}
              canRequestUpgrade={userMenuProps.canRequestUpgrade}
              isWeb={userMenuProps.isWeb}
              className="w-[280px] lg:w-[320px]"
              testId="sidebar-user-menu"
            />
          </div>
        </>
      )}
    </TooltipProvider>
  );
};

export default SideMenu;
